// Singleton logger for shared/logger.
// See react-rewrite-plan.md sections 8.1 and 11.2 platform-logger.
//
// Behavior:
// - In test mode (NODE_ENV=test or Vite MODE=test) entries are pushed into an
//   in-memory ring buffer instead of console, so tests can assert on log
//   shape without spamming output.
// - Context is always redacted through redactContext before reaching any sink.
// - .error accepts an Error (or unknown) and serializes name/message/stack.

import type { LogContext, LogEntry, LogLevel } from './types';
import { redactContext } from './redact';

const TEST_BUFFER_LIMIT = 500;
const testBuffer: LogEntry[] = [];

// Detect a test environment without depending on @types/node.
// Vite injects `import.meta.env.MODE`; Node-based runners set `NODE_ENV`.
function isTestEnv(): boolean {
  const viteMode = (import.meta as { env?: { MODE?: string } }).env?.MODE;
  if (viteMode === 'test') {
    return true;
  }
  const proc = (globalThis as { process?: { env?: Record<string, string | undefined> } }).process;
  return proc?.env?.NODE_ENV === 'test';
}

function pushToBuffer(entry: LogEntry): void {
  testBuffer.push(entry);
  if (testBuffer.length > TEST_BUFFER_LIMIT) {
    testBuffer.shift();
  }
}

function writeToConsole(entry: LogEntry): void {
  const payload =
    entry.error !== undefined
      ? { ...(entry.context ?? {}), error: entry.error }
      : (entry.context ?? {});
  switch (entry.level) {
    case 'debug':
      console.debug(entry.message, payload);
      return;
    case 'info':
      console.info(entry.message, payload);
      return;
    case 'warn':
      console.warn(entry.message, payload);
      return;
    case 'error':
      console.error(entry.message, payload);
      return;
  }
}

function serializeError(err: unknown): LogEntry['error'] {
  if (err instanceof Error) {
    return { name: err.name, message: err.message, stack: err.stack };
  }
  if (typeof err === 'string') {
    return { name: 'Error', message: err };
  }
  return { name: 'Error', message: String(err) };
}

function emit(
  level: LogLevel,
  message: string,
  context: LogContext | undefined,
  error: LogEntry['error'] | undefined,
): void {
  const entry: LogEntry = {
    level,
    message,
    timestamp: new Date().toISOString(),
  };
  const redacted = redactContext(context);
  if (redacted !== undefined) {
    entry.context = redacted;
  }
  if (error !== undefined) {
    entry.error = error;
  }
  if (isTestEnv()) {
    pushToBuffer(entry);
    return;
  }
  writeToConsole(entry);
}

export const log = {
  debug(message: string, context?: LogContext): void {
    emit('debug', message, context, undefined);
  },
  info(message: string, context?: LogContext): void {
    emit('info', message, context, undefined);
  },
  warn(message: string, context?: LogContext): void {
    emit('warn', message, context, undefined);
  },
  // Accepts (message, error, context). `error` may be an Error or any thrown value.
  error(message: string, error?: unknown, context?: LogContext): void {
    const serialized = error === undefined ? undefined : serializeError(error);
    emit('error', message, context, serialized);
  },
};

// Test-only helpers. Exported because Vitest needs to inspect entries without
// touching console. Not part of the runtime contract for app code.
export function __getTestLogs(): readonly LogEntry[] {
  return testBuffer.slice();
}

export function __clearTestLogs(): void {
  testBuffer.length = 0;
}
